import {
  Order,
  OrderDeliveryType,
  OrderStatusText,
  RouteStatus,
  RouteStatusText,
} from "@web-app/types";

export const libraries: ("geometry" | "places" | "visualization")[] = [
  "geometry",
  "places",
];

export const options = {
  clickableIcons: false,
  disableDefaultUI: true,
  disableDoubleClickZoom: false,
  gestureHandling: "greedy",
  isFractionalZoomEnabled: true,
  scrollwheel: true,
  styles: [
    {
      elementType: "labels.text.fill",
      featureType: "all",
      stylers: [
        {
          color: "#444444",
        },
      ],
    },
    {
      elementType: "labels.text.stroke",
      featureType: "all",
      stylers: [
        {
          color: "#f2f2f2",
        },
      ],
    },
    {
      elementType: "all",
      featureType: "administrative",
      stylers: [
        {
          visibility: "on",
        },
      ],
    },
    {
      elementType: "labels.text.fill",
      featureType: "administrative",
      stylers: [
        {
          color: "#444444",
        },
        {
          lightness: 45,
        },
      ],
    },
    {
      elementType: "all",
      featureType: "landscape",
      stylers: [
        {
          color: "#f2f2f2",
        },
      ],
    },
    {
      elementType: "labels.text",
      featureType: "landscape.natural.landcover",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      elementType: "all",
      featureType: "road",
      stylers: [
        {
          saturation: -100,
        },
        {
          lightness: 45,
        },
      ],
    },
    {
      elementType: "all",
      featureType: "road.highway",
      stylers: [
        {
          visibility: "simplified",
        },
      ],
    },
    {
      elementType: "labels.icon",
      featureType: "road.arterial",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      elementType: "all",
      featureType: "water",
      stylers: [
        {
          color: "#e1e1e1",
        },
        {
          visibility: "on",
        },
      ],
    },

    {
      featureType: "poi",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      elementType: "labels.icon",
      featureType: "road",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      elementType: "all",
      featureType: "transit",
      stylers: [
        {
          color: "#444444",
          visibility: "off",
        },
        {
          lightness: 45,
        },
      ],
    },
    {
      elementType: "labels.text",
      featureType: "transit",
      stylers: [
        {
          color: "#444444",
          visibility: "on",
        },
      ],
    },
    {
      elementType: "labels.icon",
      featureType: "transit",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      elementType: "all",
      featureType: "landscape",
      stylers: [
        {
          color: "#f2f2f2",
          visibility: "on",
        },
      ],
    },
    {
      elementType: "labels.text",
      featureType: "poi",
      stylers: [
        {
          color: "#444444",
          visibility: "on",
        },
        {
          lightness: 45,
        },
      ],
    },
    {
      elementType: "labels.text",
      featureType: "poi.school",
      stylers: [
        {
          color: "#444444",
          visibility: "on",
        },
        {
          lightness: 45,
        },
      ],
    },
    {
      elementType: "labels.text.stroke",
      featureType: "poi.school",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      elementType: "labels.text",
      featureType: "poi.attraction",
      stylers: [
        {
          visibility: "on",
        },
      ],
    },
    {
      elementType: "labels.text",
      featureType: "poi.business",
      stylers: [
        {
          visibility: "on",
        },
      ],
    },
    {
      elementType: "geometry",
      featureType: "poi.attraction",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      elementType: "labels.text",
      featureType: "poi.attraction",
      stylers: [
        {
          visibility: "on",
        },
      ],
    },
    {
      elementType: "labels.text",
      featureType: "poi.government",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      elementType: "labels.text",
      featureType: "poi.medical",
      stylers: [
        {
          color: "#444444",
          visibility: "on",
        },
      ],
    },
    {
      elementType: "all",
      featureType: "poi.park",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      elementType: "labels.text",
      featureType: "poi.park",
      stylers: [
        {
          color: "#444444",
          visibility: "on",
        },
        {
          lightness: 45,
        },
      ],
    },
  ],
};

export const getOrderStatus = (order: Order) => {
  return order.statusText === "Assigned" ? "ASSIGNED" : order.status;
};

const iconCache = new Map<string, string>();

export const getOrderIcon = (order: Order, type?: "DROPOFF" | "PICKUP") => {
  const cacheKey = `${order.type}-${type}-${getOrderStatus(order)}`;

  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey)!;
  }

  let iconPath: string;

  if (order.type === OrderDeliveryType.ROUTE_OPT_PICKUP) {
    iconPath = `/markers/PICKUP_${getOrderStatus(order)}.webp`;
  } else if (order.type === OrderDeliveryType.ROUTE_OPT_DROPOFF) {
    iconPath = `/markers/DROPOFF_${getOrderStatus(order)}.webp`;
  } else {
    iconPath = `/markers/${type}_${getOrderStatus(order)}.webp`;
  }

  iconCache.set(cacheKey, iconPath);

  return iconPath;
};

const driverIconCache = new Map<string, string>();

export const getDriverIcon = (dutyStatus: string) => {
  if (driverIconCache.has(dutyStatus)) {
    return driverIconCache.get(dutyStatus)!;
  }

  const iconPath = `/markers/driver_${dutyStatus}.webp`;

  driverIconCache.set(dutyStatus, iconPath);

  return iconPath;
};

export const getClusterIcon = () => {
  return `/markers/cluster-marker.webp`;
};

export const clearIconCaches = () => {
  iconCache.clear();
  driverIconCache.clear();
};

export const maintainIconCaches = () => {
  const MAX_CACHE_SIZE = 100;
  if (iconCache.size > MAX_CACHE_SIZE) {
    iconCache.clear();
  }
  if (driverIconCache.size > MAX_CACHE_SIZE) {
    driverIconCache.clear();
  }
};

export const getOrderLocation = (order: Order, type?: "DROPOFF" | "PICKUP") => {
  if (order.type === OrderDeliveryType.ROUTE_OPT_PICKUP) {
    return order.pickupLocation;
  } else if (order.type === OrderDeliveryType.ROUTE_OPT_DROPOFF) {
    return order.dropoffLocation;
  } else {
    if (type === "PICKUP") {
      return order.pickupLocation;
    } else {
      return order.dropoffLocation;
    }
  }
};

export const ORDER_STATUS_ROUTE_COLOR: Record<Order["statusText"], string> = {
  "": "#141414",
  Assigned: "#9747FF",
  Cancelled: "#FD4242",
  Completed: "#008760",
  Failed: "#FD4242",
  "In Transit": "#2463eb",
  "In Transit to Drop-off": "#2463eb",
  "In Transit to Pickup": "#2463eb",
  Unassigned: "#141414",
};

export const ROUTE_STATUS_ROUTE_COLOR: Record<RouteStatusText, string> = {
  Assigned: "#9747FF",
  Completed: "#008760",
  "In Transit": "#2463eb",
  "Not Started": "#141414",
};

export const getOrderOpacity = (
  focusedRouteId: string | null,
  mapFocusedOrderId: string | null,
  orderId: string,
  hoveredOrderId: string | null,
) => {
  if (focusedRouteId) {
    return 0.3;
  }

  if (hoveredOrderId && !mapFocusedOrderId) {
    if (hoveredOrderId === orderId) {
      return 1;
    } else {
      return 0.3;
    }
  }

  if (mapFocusedOrderId) {
    if (orderId === mapFocusedOrderId) {
      return 1;
    } else if (hoveredOrderId && hoveredOrderId === orderId) {
      return 0.7;
    } else {
      return 0.3;
    }
  }

  return 1;
};

export const getPolylineOptions = (
  opacity: number,
  statusText: OrderStatusText,
) => ({
  strokeColor: ORDER_STATUS_ROUTE_COLOR[statusText],
  strokeOpacity: opacity,
  strokeWeight: 2,
});

export const getRoutePolylineOptions = (
  status: RouteStatus,
  opacity: number,
  isAssigned?: boolean,
) => ({
  strokeColor: ROUTE_STATUS_ROUTE_COLOR[getRouteStatusText(status, isAssigned)],
  strokeOpacity: opacity,
  strokeWeight: 2,
});

export const getDashedPolylineOptions = (
  statusText: OrderStatusText,
  opacity: number,
) => ({
  ...getPolylineOptions(opacity, statusText),
  strokeOpacity: 0,
});

export const getRouteForwardArrow = (
  opacity: number,
  status: RouteStatus,
  isAssigned?: boolean,
) => ({
  fillColor: ROUTE_STATUS_ROUTE_COLOR[getRouteStatusText(status, isAssigned)],
  fillOpacity: opacity,
  path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
  strokeOpacity: opacity,
  strokeWeight: 2,
});

export const getForwardArrow = (
  opacity: number,
  statusText: OrderStatusText,
) => ({
  fillColor: ORDER_STATUS_ROUTE_COLOR[statusText],
  fillOpacity: opacity,
  path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
  strokeOpacity: opacity,
  strokeWeight: 2,
});

export const getCircleIcon = (
  opacity: number,
  statusText: OrderStatusText,
) => ({
  fillColor: ORDER_STATUS_ROUTE_COLOR[statusText],
  fillOpacity: opacity,
  path: google.maps.SymbolPath.CIRCLE,
  strokeOpacity: opacity,
  strokeWeight: 2,
});

export const getDashedLineIcon = (
  opacity: number,
  statusText: OrderStatusText,
) => ({
  path: "M 0,-1 0,1",
  scale: 4,
  strokeColor: ORDER_STATUS_ROUTE_COLOR[statusText],
  strokeOpacity: opacity,
  strokeWeight: 1,
});

export const getOrderStatusFromRouteStatus = (
  routeStatus: RouteStatusText,
): OrderStatusText => {
  switch (routeStatus) {
    case "Not Started":
      return "Unassigned";
    case "In Transit":
      return "In Transit";
    case "Completed":
      return "Completed";
    default:
      return "Unassigned";
  }
};

export const getRouteStatusText = (
  status: RouteStatus,
  isAssignee?: boolean,
) => {
  switch (status) {
    case "INPROGRESS":
      return "In Transit";
    case "COMPLETE":
      return "Completed";
    default:
      if (isAssignee) {
        return "Assigned";
      } else {
        return "Not Started";
      }
  }
};

export const getRoutedOrderOpacity = (
  focusedRouteId: string | null,
  focusedOrderId: string | null,
  hoveredOrderId: string | null,
  orders: Map<string, Order>,
  routeId: string,
) => {
  if (!focusedRouteId && !focusedOrderId && !hoveredOrderId) {
    return 1;
  }

  if (focusedRouteId === routeId) {
    return 1;
  }

  if (focusedOrderId && !hoveredOrderId) {
    const order = orders.get(focusedOrderId);
    if (!order) {
      return 0.3;
    }

    if (order.routeId === routeId) {
      return 1;
    }
  }

  if (hoveredOrderId) {
    const order = orders.get(hoveredOrderId);
    if (!order) {
      return 0.3;
    }

    if (focusedOrderId || focusedRouteId) {
      if (order.routeId === routeId) {
        return 0.7;
      }
    } else {
      if (order.routeId === routeId) {
        return 1;
      }
    }
  }

  return 0.3;
};

export const getRoutedOrderPosition = (
  focusedRouteId: string | null,
  focusedOrderId: string | null,
  hoveredOrderId: string | null,
  orders: Map<string, Order>,
  routeId: string,
) => {
  const left = "top-3 left-3";
  const right = "top-3 right-10";

  if (!focusedRouteId && !focusedOrderId && !hoveredOrderId) {
    return left;
  }

  if (focusedRouteId === routeId) {
    return left;
  }

  if (focusedOrderId && !hoveredOrderId) {
    const order = orders.get(focusedOrderId);
    if (!order) {
      return left;
    }

    if (order.routeId === routeId) {
      return left;
    }
  }

  if (hoveredOrderId) {
    const order = orders.get(hoveredOrderId);
    if (!order) {
      return left;
    }

    if (focusedOrderId || focusedRouteId) {
      const focusedOrder = orders.get(focusedOrderId || "");

      if (
        focusedOrder &&
        focusedOrder.routeId === order.routeId &&
        order.routeId === routeId
      ) {
        return left;
      }

      if (order.routeId === routeId) {
        return right;
      }
    } else {
      if (order.routeId === routeId) {
        return left;
      }
    }
  }

  return left;
};
